import type {
  AboutYourselfValidationResult,
  AddressLike,
  AlternateAddressLike,
  AboutYourselfFormData,
  PreviousAddressRow,
} from '../types/utils'
import type { AboutYourselfAddressEditFormData } from '../types/tabs'
import { coalesceAddressLikeForRules } from '../components/tabs/aboutYourselfTabHelpers'
import {
  validateMortgagePaymentAmount,
  validateRentAmountForSubmit,
} from './rentAmountValidators'
import { getAddressMonth, checkPreviousAddress } from "./previousAddressLogic"
import { validateDateOfBirth, parseLocalDateString } from "@/lib/utils/dateValidation"
import { calendarMonthsBetween, formatDateForAPI } from "@/features/applicant/utils/addressHelpers"
import {
  ANGULAR_NAME_PATTERN,
  nameHasOuterWhitespace,
} from "./aboutYourselfAngularInlineMessages"

const ZIP_DIGITS = /^\d{5,6}$/
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_DIGITS_MIN = 10

const TWENTY_FOUR_MONTHS = 24
const MASKED_SSN_PATTERN = /^X{3}-X{2}-\d{4}$/i





const trim = (value: string | undefined | null): string => (value ?? "").toString().trim()

const isOutsideUS = (addr: AddressLike | undefined): boolean => {
  if (!addr) return false
  if (addr.outside_us === true || addr.outside_us === "1") return true
  const country = (addr.country || "US").toUpperCase()
  return country !== "US" && country !== "USA"
}

const isStillLiving = (ad: AddressLike["address_data"] | undefined): boolean => {
  const flag = ad?.currently_living
  return flag === true || flag === "1"
}

/** Normalize API / input date strings to YYYY-MM-DD for calendar math. */
function toYmdForCalendar(dateStr: string | undefined | null): string {
  const raw = trim(dateStr)
  if (!raw) return ""
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10)
  return formatDateForAPI(raw)
}

/** 24-month history months from stored field or calendar diff (Angular/applicant parity). */
export function getHistoryMonthsAtAddress(addr: AddressLike | undefined): number {
  if (!addr) return 0
  const ad = addr.address_data ?? {}
  const type = trim(addr.address_type).toUpperCase()
  const raw = type === "OWN" ? ad.month_reside : ad.paid_rent
  const n = Number(raw)
  if (Number.isFinite(n) && n > 0) return n

  const moveIn = toYmdForCalendar(ad.moveIn_date)
  if (!moveIn) return Number.isFinite(n) ? n : 0

  const living = isStillLiving(ad)
  const moveOutRaw = trim(ad.moveOut_date || "")
  const endYmd = living || !moveOutRaw ? undefined : toYmdForCalendar(moveOutRaw)
  const fromDates = calendarMonthsBetween(moveIn, endYmd)
  if (fromDates > 0) return fromDates
  return Number.isFinite(n) ? n : 0
}

const monthsForCurrentAddress = (addr: AddressLike | undefined): number =>
  getAddressMonth(addr)

const monthsForPreviousAddress = (addr: AddressLike | undefined): number =>
  getAddressMonth(addr)

/** Angular `aboutyourselfinfo.current_address.valid` gate for previous-address UI. */
export function isCurrentAddressValidForPreviousSection(
  addr: AddressLike | AboutYourselfAddressEditFormData | undefined | null,
): boolean {
  const normalized = coalesceAddressLikeForRules(addr, true)
  if (!normalized) return false
  const errors: string[] = []
  validateAddressBase(normalized, errors, "")
  validateAddressByType(normalized, errors, "", true)

  return errors.length === 0
}

const validatePersonalInfo = (data: AboutYourselfFormData, errors: string[]) => {
  const rawFirst = (data.first_name ?? "").toString()
  const first = trim(data.first_name)
  if (!first) errors.push("First Name is required")
  else if (nameHasOuterWhitespace(rawFirst) || !ANGULAR_NAME_PATTERN.test(first)) {
    errors.push("First Name is not valid")
  }

  const rawMiddle = (data.middle_name ?? "").toString()
  const middle = trim(data.middle_name)
  if (middle) {
    if (nameHasOuterWhitespace(rawMiddle) || !ANGULAR_NAME_PATTERN.test(middle)) {
      errors.push("Middle Name is not valid")
    }
  }

  const rawLast = (data.last_name ?? "").toString()
  const last = trim(data.last_name)
  if (!last) errors.push("Last Name is required")
  else if (nameHasOuterWhitespace(rawLast) || !ANGULAR_NAME_PATTERN.test(last)) {
    errors.push("Last Name is not valid")
  }

  const phoneDigits = trim(data.mobile_no).replace(/\D/g, "")
  if (!phoneDigits) {
    errors.push("Mobile Phone is required")
  } else if (phoneDigits.length < PHONE_DIGITS_MIN) {
    errors.push("Mobile Phone must be at least 10 digits")
  }

  const email = trim(data.email)
  if (!email) {
    errors.push("Email is required")
  } else if (!EMAIL_PATTERN.test(email)) {
    errors.push("Email — Enter Valid Email")
  }

  if (!trim(data.communication_preference)) {
    errors.push("Communication Preference is required")
  }

  const dob = trim(data.birthdate)
  if (!dob) {
    errors.push("Date of Birth is required")
  } else {
    const dobValidation = validateDateOfBirth(dob)
    if (!dobValidation.isValid) {
      errors.push(`Date of Birth: ${dobValidation.error || "Invalid date of birth"}`)
    }
  }

  const ssnRaw = trim(data.security_number)
  const ssnDigits = ssnRaw.replace(/\D/g, "")
  if (!ssnRaw) {
    errors.push("SSN or ITIN is required")
  } else if (MASKED_SSN_PATTERN.test(ssnRaw)) {
    // Masked SSN (XXX-XX-1234) is valid in edit mode — unmasked value tracked separately.
  } else if (ssnDigits.length !== 9) {
    errors.push("SSN or ITIN must be 9 digits")
  }
}

export const validateAddressBase = (
  addr: AddressLike,
  errors: string[],
  prefix: string,
) => {
  // Non-US: skip strict numeric-street / zip format checks.
  const isOutside = isOutsideUS(addr)
  const streetNumber = trim(addr.house_no)
  if (!streetNumber) {
    errors.push(`${prefix}Street No. is required`)
  }

  if (!trim(addr.street)) errors.push(`${prefix}Street Name is required`)
  if (!trim(addr.city)) errors.push(`${prefix}City is required`)
  if (!trim(addr.state)) errors.push(`${prefix}State is required`)

  const zip = trim(addr.zip_code)
  if (!zip) {
    errors.push(`${prefix}Zipcode is required`)
  } else if (!isOutside && !ZIP_DIGITS.test(zip.replace(/\D/g, ""))) {
    errors.push(`${prefix}Zipcode must be 5 or 6 digits`)
  }

  if (!trim(addr.address_type)) {
    errors.push(`${prefix}Type of residence is required`)
  }
}

const validateAddressMoveTimeline = (
  ad: NonNullable<AddressLike["address_data"]>,
  errors: string[],
  prefix: string,
) => {
  const miRaw = trim(ad.moveIn_date)
  if (!miRaw) return
  const ymdIn = toYmdForCalendar(miRaw)
  const dIn = parseLocalDateString(ymdIn)
  if (!dIn) {
    errors.push(`${prefix}Move In Date must be a valid date`)
    return
  }
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  if (dIn.getTime() > today.getTime()) {
    errors.push(`${prefix}Move In Date cannot be in the future`)
  }

  const moRaw = trim(ad.moveOut_date || "")
  if (!moRaw) return
  const ymdOut = toYmdForCalendar(moRaw)
  const dOut = parseLocalDateString(ymdOut)
  if (!dOut) {
    errors.push(`${prefix}Move Out / Lease End Date must be a valid date`)
    return
  }
  if (dOut.getTime() < dIn.getTime()) {
    errors.push(`${prefix}Move Out / Lease End Date must be on or after Move In Date`)
  }
}

const validateRentFields = (
  ad: NonNullable<AddressLike["address_data"]>,
  errors: string[],
  prefix: string,
  isCurrent: boolean,
) => {
  if (!trim(ad.moveIn_date)) errors.push(`${prefix}Move In Date is required`)

  validateAddressMoveTimeline(ad, errors, prefix)

  const landlordName = trim(ad.landlord_name)
  if (!landlordName) errors.push(`${prefix}Landlord Name is required`)

  const landlordEmail = trim(ad.landlord_email)
  if (!landlordEmail) {
    errors.push(`${prefix}Landlord Email is required`)
  } else if (!EMAIL_PATTERN.test(landlordEmail)) {
    errors.push(`${prefix}Landlord Email — Enter Valid Email`)
  }

  const landlordPhoneDigits = trim(ad.landlord_mobile_no).replace(/\D/g, "")
  if (!landlordPhoneDigits) {
    errors.push(`${prefix}Landlord Mobile is required`)
  } else if (landlordPhoneDigits.length < PHONE_DIGITS_MIN) {
    errors.push(`${prefix}Landlord Mobile — Enter Valid Phone Number`)
  }

  const rentMsg = validateRentAmountForSubmit(
    ad.rent_amount == null ? "" : String(ad.rent_amount),
  )
  if (rentMsg) errors.push(`${prefix}${rentMsg}`)

  if (isCurrent) {
    const duration = ad.paid_rent
    if (duration === undefined || duration === null || String(duration).trim() === "") {
      errors.push(`${prefix}Duration is required`)
    }
  }

  if (isCurrent && !isStillLiving(ad) && !trim(ad.moveOut_date)) {
    errors.push(`${prefix}Lease End Date is required`)
  }
}

const validateOwnFields = (
  ad: NonNullable<AddressLike["address_data"]>,
  errors: string[],
  prefix: string,
  isCurrent: boolean,
) => {
  if (!trim(ad.moveIn_date)) errors.push(`${prefix}Move In Date is required`)

  validateAddressMoveTimeline(ad, errors, prefix)

  if (isCurrent) {
    if (!trim(ad.rent_amount)) {
      errors.push(`${prefix}Monthly Mortgage Payment is required`)
    } else {
      const mErr = validateMortgagePaymentAmount(String(ad.rent_amount))
      if (mErr) errors.push(`${prefix}${mErr}`)
    }
    const duration = ad.month_reside
    if (duration === undefined || duration === null || String(duration).trim() === "") {
      errors.push(`${prefix}Duration is required`)
    }
  }
  if (isCurrent && !isStillLiving(ad) && !trim(ad.moveOut_date)) {
    errors.push(`${prefix}Planned Move Out Date is required`)
  }
}

const validateFamilyFields = (
  ad: NonNullable<AddressLike["address_data"]>,
  errors: string[],
  prefix: string,
  isCurrent: boolean,
) => {
  if (!trim(ad.moveIn_date)) errors.push(`${prefix}Move In Date is required`)

  validateAddressMoveTimeline(ad, errors, prefix)

  if (!trim(ad.landlord_name)) errors.push(`${prefix}Contact Name is required`)

  const contactEmail = trim(ad.landlord_email)
  if (!contactEmail) {
    errors.push(`${prefix}Contact Email is required`)
  } else if (!EMAIL_PATTERN.test(contactEmail)) {
    errors.push(`${prefix}Contact Email — Enter Valid Email`)
  }

  const contactPhoneDigits = trim(ad.landlord_mobile_no).replace(/\D/g, "")
  if (!contactPhoneDigits) {
    errors.push(`${prefix}Contact Mobile is required`)
  } else if (contactPhoneDigits.length < PHONE_DIGITS_MIN) {
    errors.push(`${prefix}Contact Mobile — Enter Valid Phone Number`)
  }

  const rentMsg = validateRentAmountForSubmit(
    ad.rent_amount == null ? "" : String(ad.rent_amount),
  )
  if (rentMsg) errors.push(`${prefix}${rentMsg}`)

  if (isCurrent) {
    const duration = ad.paid_rent
    if (duration === undefined || duration === null || String(duration).trim() === "") {
      errors.push(`${prefix}Duration is required`)
    }
  }

  if (!trim(ad.landlord_relationship_type)) {
    errors.push(`${prefix}Contact Relationship is required`)
  }

  if (isCurrent && !isStillLiving(ad) && !trim(ad.moveOut_date)) {
    errors.push(`${prefix}Planned Move Out Date is required`)
  }
}

export const validateAddressByType = (
  addr: AddressLike,
  errors: string[],
  prefix: string,
  isCurrent: boolean,
) => {
  const type = trim(addr.address_type).toUpperCase()
  const ad = addr.address_data ?? {}
  if (type === "RENT") {
    validateRentFields(ad, errors, prefix, isCurrent)
  } else if (type === "OWN") {
    validateOwnFields(ad, errors, prefix, isCurrent)
  } else if (type === "FAMILY") {
    validateFamilyFields(ad, errors, prefix, isCurrent)
  }
}

const validateAlternateAddress = (
  addr: AlternateAddressLike,
  errors: string[],
  index: number,
) => {
  const hasAnyField =
    trim(addr.street_address) ||
    trim(addr.house_no) ||
    trim(addr.street) ||
    trim(addr.city) ||
    trim(addr.state) ||
    trim(addr.zip_code)

  if (!hasAnyField) return

  const prefix = `Alternate Address ${index + 1} - `

  // Non-US: skip strict numeric-street / zip format checks.
  const isOutside = isOutsideUS(addr)
  const streetNumber = trim(addr.house_no)
  if (!streetNumber) {
    errors.push(`${prefix}Street No. is required`)
  }

  if (!trim(addr.street)) errors.push(`${prefix}Street Name is required`)
  if (!trim(addr.city)) errors.push(`${prefix}City is required`)
  if (!trim(addr.state)) errors.push(`${prefix}State is required`)

  const zip = trim(addr.zip_code)
  if (!zip) {
    errors.push(`${prefix}Zipcode is required`)
  } else if (!isOutside && !ZIP_DIGITS.test(zip.replace(/\D/g, ""))) {
    errors.push(`${prefix}Zipcode must be 5 or 6 digits`)
  }
}

export function validateAboutYourselfTab(
  data: AboutYourselfFormData,
): AboutYourselfValidationResult {
  const errors: string[] = []

  validatePersonalInfo(data, errors)

  if (data.current_address) {
    validateAddressBase(data.current_address, errors, "")
    validateAddressByType(data.current_address, errors, "", true)

    // Angular: previous-address / 24-month rules only when `checkPreviousAddress()` is true
    // (`paid_rent` / `month_reside` control value < 24, including explicit 0).
    if (checkPreviousAddress(data.current_address as PreviousAddressRow)) {
      const currentMonths = monthsForCurrentAddress(data.current_address)
      if (currentMonths < TWENTY_FOUR_MONTHS) {
        const prevList = data.previous_address ?? []
        if (prevList.length === 0) {
          errors.push(
            `You must provide at least 24 months of address history. Current address covers ${currentMonths} month(s); please add a previous address.`,
          )
        } else {
          let cumulative = currentMonths
          prevList.forEach((prev, index) => {
            const label = `Previous Address ${index + 1} - `
            if (!trim(prev.address_type)) {
              errors.push(`${label}Type of residence is required`)
            }
            validateAddressBase(prev, errors, label)
            validateAddressByType(prev, errors, label, false)
            cumulative += monthsForPreviousAddress(prev)
          })
          if (cumulative < TWENTY_FOUR_MONTHS) {
            errors.push(
              `You must provide at least 24 months of address history. Total provided: ${cumulative} month(s).`,
            )
          }
        }
      }
    }
  }

  if (data.alternate_address && data.alternate_address.length > 0) {
    data.alternate_address.forEach((addr, index) => {
      validateAlternateAddress(addr, errors, index)
    })
  }

  return { isValid: errors.length === 0, errors }
}

const SHARED_PHONE_KEYWORDS = ["registered", "exists", "already in use", "duplicate"]

export function isSharedPhoneError(message: string | undefined | null): boolean {
  if (!message) return false
  const lower = message.toLowerCase()
  return SHARED_PHONE_KEYWORDS.some((keyword) => lower.includes(keyword))
}

export function isOutsideUSWithoutAlternate(
  data: AboutYourselfFormData,
): boolean {
  if (!data.current_address) return false
  if (!isOutsideUS(data.current_address)) return false
  if (data.non_us_address === "1") return false
  const alts = data.alternate_address ?? []
  return alts.length === 0
}

export type { AboutYourselfFormData } from '../types/utils'

